import React from 'react';
import { Controller } from 'react-hook-form';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Input from '@amzn/meridian/input';
import { useTranslation } from 'src/arb-wrapper';
import { FormInput } from 'src/components/common/forms';
import { FormSelect } from 'src/components/common/forms/FormSelect';
import { getPublisherTimezoneOptions } from 'src/components/common/forms/TimezoneSelect';
import { getCurrencyOptions, currencyMinDefault, currencySymbol } from 'src/components/common/forms/currencyOptions';
import { BrandingSelector } from 'src/components/publisher-config/BrandingSelector';
import { BrandBadge } from 'src/components/publisher-config/BrandBadge';
import { useBrandControllerGetAvailablePresetsQuery } from 'src/store/leela-api';
import appConfig from 'src/config';
import type { Control, FieldValues, UseFormWatch, UseFormSetValue } from 'react-hook-form';

import { ConfigurationType } from 'src/constants';

interface PublisherAccountFieldsProps {
  publisher: string;
  /** When provided, renders form inputs. When omitted, renders read-only text. */
  control?: Control<FieldValues>;
  /** Read-only values, used when control is not provided */
  values?: Record<string, unknown>;
  /** Form watch function — required when control is provided (for branding selector). */
  watch?: UseFormWatch<FieldValues>;
  /** Form setValue function — required when control is provided (for branding selector). */
  setValue?: UseFormSetValue<FieldValues>;
  /** Hide the account minDailyBudget field (onboarding-only override) — e.g. on the campaign setup page. */
  hideMinDailyBudget?: boolean;
}

interface PublisherFieldDescriptor {
  key: string;
  labelKey: string;
  /** Field input type. Defaults to a plain text input; 'timezone'/'currency' render dropdowns, 'number' a numeric input. */
  type?: 'timezone' | 'currency' | 'number';
  /** When true the field is not required (defaults to required). */
  optional?: boolean;
}

// Every publisher account config carries an account-level IANA timezone.
const TIMEZONE_FIELD: PublisherFieldDescriptor = { key: 'timezone', labelKey: 'publisher_config_timezone', type: 'timezone' };
// Google accounts require a billing currency chosen at onboarding (budgets/bids are in this currency).
const CURRENCY_FIELD: PublisherFieldDescriptor = { key: 'currency', labelKey: 'publisher_config_currency', type: 'currency' };
// Optional per-account override of the daily-budget minimum. Left blank by default: the currency's
// default (shown as helper text) is applied by the backend unless the user enters an override.
const MIN_BUDGET_FIELD: PublisherFieldDescriptor = { key: 'minDailyBudget', labelKey: 'publisher_config_min_daily_budget', type: 'number', optional: true };

export const FIELD_CONFIG: Record<string, PublisherFieldDescriptor[]> = {
  meta: [{ key: 'catalogId', labelKey: 'publisher_config_catalog_id' }, TIMEZONE_FIELD],
  tiktok: [
    { key: 'catalogId', labelKey: 'publisher_config_catalog_id' },
    { key: 'businessCenterId', labelKey: 'publisher_config_business_center_id' },
    TIMEZONE_FIELD,
  ],
  adsp: [
    { key: 'advertiserId', labelKey: 'publisher_config_advertiser_id' },
    { key: 'userId', labelKey: 'publisher_config_user_id' },
    TIMEZONE_FIELD,
  ],
  google: [
    { key: 'mccAccountId', labelKey: 'publisher_config_mcc_account_id' },
    { key: 'customerAccountId', labelKey: 'publisher_config_customer_account_id' },
    TIMEZONE_FIELD,
    CURRENCY_FIELD,
    MIN_BUDGET_FIELD,
  ],
};

/** Chunks fields into rows of two so they render as a clean 2-column grid. */
function toRows (fields: PublisherFieldDescriptor[]): PublisherFieldDescriptor[][] {
  const rows: PublisherFieldDescriptor[][] = [];
  for (let i = 0; i < fields.length; i += 2) {
    rows.push(fields.slice(i, i + 2));
  }
  return rows;
}

export const PublisherAccountFields: React.FC<PublisherAccountFieldsProps> = ({ publisher, control, values, watch, setValue, hideMinDailyBudget }) => {
  const { t } = useTranslation();
  const { data: brandPresetsData, isLoading: isBrandPresetsLoading } = useBrandControllerGetAvailablePresetsQuery(undefined, { skip: publisher !== 'google' });
  const allFields = FIELD_CONFIG[publisher];
  // minDailyBudget is an onboarding-only override; hide it in identity-only contexts (e.g. campaign setup).
  const fields = allFields && hideMinDailyBudget ? allFields.filter(f => f.key !== 'minDailyBudget') : allFields;

  // Currently-selected currency drives the minDailyBudget helper text (the per-currency default the
  // backend applies when the field is left blank). We do NOT prefill the field — leaving it empty means
  // "use the default"; a value is submitted only when the user explicitly overrides.
  const watchedCurrency = watch ? (watch('currency') as string | undefined) : undefined;
  const minBudgetDefault = watchedCurrency ? currencyMinDefault(watchedCurrency) : undefined;
  const minBudgetHelperText = minBudgetDefault != null
    ? `${currencySymbol(watchedCurrency)}${minBudgetDefault} · ${t('publisher_config_min_daily_budget_helper')}`
    : t('publisher_config_min_daily_budget_helper');

  if (!fields) return null;

  const rows = toRows(fields);

  if (control) {
    return (
      <Column spacing="300">
        {rows.map((rowFields, idx) => (
          <Row key={idx} widths={['fill', 'fill']}>
            {rowFields.map(f => {
              if (f.type === 'timezone' || f.type === 'currency') {
                return (
                  <FormSelect
                    control={control}
                    data-testid={`publisher-config-${f.key}`}
                    key={f.key}
                    label={t(f.labelKey)}
                    name={f.key}
                    options={f.type === 'currency' ? getCurrencyOptions() : getPublisherTimezoneOptions()}
                    required
                    width="100%"
                  />
                );
              }
              if (f.type === 'number') {
                // Numeric fields (e.g. minDailyBudget) render as text + inputMode="decimal": a native
                // type="number" applies an implicit step=1 that rejects decimals (4.4, 3.8, ...), and
                // FormInput's minLength=3 rule would reject short values like "7". We hold the raw string
                // and coerce to a Number at submit (buildPublisherAccountConfig). The currency default is
                // shown as helper text only — the field stays blank unless the user overrides.
                return (
                  <Controller
                    control={control}
                    key={f.key}
                    name={f.key}
                    render={({ field, fieldState }) => (
                      <Input
                        data-testid={`publisher-config-${f.key}`}
                        error={!!fieldState.error}
                        errorMessage={fieldState.error?.message}
                        helperText={f.key === 'minDailyBudget' ? minBudgetHelperText : undefined}
                        inputMode="decimal"
                        label={t(f.labelKey)}
                        type="text"
                        value={field.value == null || field.value === '' ? '' : String(field.value)}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                      />
                    )}
                  />
                );
              }
              return <FormInput control={control} key={f.key} label={t(f.labelKey)} name={f.key} required={!f.optional} />;
            })}
            {rowFields.length === 1 && <div />}
          </Row>
        ))}
        {publisher === 'google' && watch && setValue && (
          <BrandingSelector
            disabled={isBrandPresetsLoading}
            mediaBucketName={appConfig.mediaBucketName}
            presets={brandPresetsData?.presets ?? []}
            selectedBrandType={watch('brandType') as string}
            onChange={(brandType: string) => { setValue('brandType', brandType); }}
          />
        )}
      </Column>
    );
  }

  return (
    <Column spacing="300">
      {rows.map((rowFields, idx) => (
        <Row key={idx} widths={['fill', 'fill']}>
          {rowFields.map(f => (
            <Column key={f.key} spacing="100">
              <Text color="secondary" type="b200">{t(f.labelKey)}</Text>
              {/* Legacy Google configs may have no stored currency; the backend resolves them to USD. */}
              <Text>{String(values?.[f.key] ?? (f.key === 'currency' ? 'USD' : ''))}</Text>
            </Column>
          ))}
          {rowFields.length === 1 && <div />}
        </Row>
      ))}
      {publisher === 'google' && !!values?.brandType && (
        (() => {
          const preset = (brandPresetsData?.presets ?? []).find(p => p.brandType === values.brandType);
          return preset
            ? <BrandBadge brandType={preset.brandType} businessName={preset.businessName} logoS3Key={preset.logoS3Key} mediaBucketName={appConfig.mediaBucketName} />
            : <Text color="secondary">{String(values.brandType)}</Text>;
        })()
      )}
    </Column>
  );
};

/** Builds a PUBLISHER_ACCOUNT config object from form data and publisher. */
export function buildPublisherAccountConfig (
  publisher: string,
  formData: Record<string, unknown>,
): Record<string, unknown> {
  const fields = FIELD_CONFIG[publisher] ?? [];
  const config: Record<string, unknown> = {
    configType: ConfigurationType.PUBLISHER_ACCOUNT,
    publisher,
  };
  for (const { key } of fields) {
    config[key] = formData[key];
  }
  // brandType is set by the BrandingSelector card picker, not a form field
  if (formData.brandType) {
    config.brandType = formData.brandType;
  }
  // minDailyBudget is an optional numeric override: coerce to a number, or omit when blank so the
  // backend applies the currency default.
  if (config.minDailyBudget === '' || config.minDailyBudget == null || isNaN(Number(config.minDailyBudget))) {
    delete config.minDailyBudget;
  } else {
    config.minDailyBudget = Number(config.minDailyBudget);
  }
  return config;
}
